import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaExclamationCircle } from 'react-icons/fa';
import { IconBaseProps } from 'react-icons';

const NotificationContainer = styled(motion.div)`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Notification = styled(motion.div)`
  background: var(--bg-secondary);
  border: 1px solid var(--accent-color);
  border-radius: 8px;
  padding: 1rem 1.25rem;
  min-width: 300px;
  max-width: 400px;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(8px);

  @media (max-width: 768px) {
    min-width: 250px;
    padding: 0.875rem 1rem;
  }
`;

const IconWrapper = styled.div`
  color: #ff4d4d;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  margin-top: 2px;
`;

const Content = styled.div`
  flex: 1;
`;

const Title = styled.div`
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
  font-size: 0.95rem;
`;

const Message = styled.div`
  color: var(--text-secondary);
  font-size: 0.9rem;
  line-height: 1.4;
`;

const ProgressBar = styled(motion.div)`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 2px;
  background: #ff4d4d;
  border-radius: 0 0 8px 8px;
  transform-origin: left;
`;

interface ErrorNotificationProps {
  message: string;
  title?: string;
  duration?: number;
  onClose: () => void;
}

const ErrorNotification: React.FC<ErrorNotificationProps> = ({
  message,
  title = 'Error',
  duration = 5000,
  onClose,
}) => {
  React.useEffect(() => {
    const timer = setTimeout(onClose, duration);
    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const Icon = FaExclamationCircle as React.ComponentType<IconBaseProps>;

  return (
    <NotificationContainer
      initial={{ opacity: 0, x: 100 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 100 }}
      transition={{ duration: 0.3 }}
    >
      <Notification>
        <IconWrapper>
          <Icon size={18} />
        </IconWrapper>
        <Content>
          <Title>{title}</Title>
          <Message>{message}</Message>
        </Content>
        <ProgressBar
          initial={{ scaleX: 1 }}
          animate={{ scaleX: 0 }}
          transition={{ duration: duration / 1000, ease: 'linear' }}
        />
      </Notification>
    </NotificationContainer>
  );
};

export default ErrorNotification; 